import './ValidateEmail.scss';

import React, { useState, useEffect, forwardRef } from 'react';
import validator from 'validator';
import { FormattedText, formatText } from 'core/i18n';
import { InputText } from 'components/inputText/inputText';
import { EMAIL_OTP_VALIDATION_RESPONSE_STATUS } from '../../sections/constants';
import { EmailOTPValidationActions } from '../../sections/EmailOTPValidation/EmailOTPValidationReducer';
import { LoadingDots } from 'commonsComponents/loadingDots';
import {
    doTagEmailFieldFocus,
    doTagSameEmailError,
    doTagValidateEmail,
} from '../../sections/EmailOTPValidation/tagging';

const { OK, BLOCKED } = EMAIL_OTP_VALIDATION_RESPONSE_STATUS;

const isRequired = value => value && value.length;
const isValidEmail = value => validator.isEmail(value);

export const ValidateEmail = forwardRef(
    ({ dispatch, formActions, sendEmailOTP, readyCaptchaToken, userProfile, userProfileForm }, ref) => {
        const [isLoading, setIsLoading] = useState(false);
        const [isButtonDisabled, setIsButtonDisabled] = useState(true);

        const handleValidation = value => {
            formActions.change('userProfile.emailConfirmation', '');
            setIsButtonDisabled(!isValidEmail(value));

            return isValidEmail(value);
        };

        const handleRequired = value => {
            setIsButtonDisabled(!isRequired(value));

            return isRequired(value);
        };

        useEffect(() => {
            if (typeof userProfileForm?.email?.errors === 'string') {
                setIsButtonDisabled(true);
            }
        }, [userProfileForm?.email?.errors]);

        const handleSendOTPEmail = async recaptcha => {
            setIsButtonDisabled(true);
            setIsLoading(true);

            await new Promise(resolve => {
                setTimeout(resolve, 300);
            });

            const response = await sendEmailOTP(recaptcha);

            try {
                if (response.status === OK) {
                    return dispatch({ type: EmailOTPValidationActions.VALIDATE_EMAIL });
                }

                if (response.status === BLOCKED) {
                    doTagSameEmailError();

                    return formActions.setErrors(
                        'userProfile.email',
                        formatText('emailValidation-introduceAnotherEmail')
                    );
                }

                return formActions.setErrors('userProfile.email', formatText('otpEmailValidation-genericError'));
            } catch {
                return formActions.setErrors('userProfile.email', formatText('otpEmailValidation-genericError'));
            } finally {
                setIsLoading(false);
            }
        };

        const handleOnClick = () => {
            doTagValidateEmail();
            readyCaptchaToken(handleSendOTPEmail);
        };

        const buttonClassName = `validate-email__btn${isButtonDisabled ? '--disabled' : ''}`;

        const validateEmailButton = (
            <button type="button" disabled={isButtonDisabled} className={buttonClassName} onClick={handleOnClick}>
                {isLoading ? (
                    <LoadingDots inline size="large" color="dark" />
                ) : (
                    <FormattedText value="email-validateEmail" />
                )}
            </button>
        );

        return (
            <div className="validate-email__container">
                <div className="validate-email__input">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="validate-email__label" htmlFor="emailField">
                        <FormattedText value="field-email" />
                    </label>
                    <p className="validate-email__sublabel">
                        <FormattedText value="email-informationAndComunications" />
                    </p>
                    <InputText
                        innerRef={ref}
                        profileModel="userProfile.email"
                        type="email"
                        id="emailField"
                        name="emailField"
                        placeholder={formatText('placeholder-email')}
                        defaultValue={userProfile.email}
                        noFocusFromLabel
                        validations={{
                            required: handleRequired,
                            valid: handleValidation,
                        }}
                        validateOnChange
                        debounceValidationTime={500}
                        messagesError={{
                            required: formatText('validation-fieldRequired'),
                            valid: formatText('validation-email'),
                        }}
                        userProfileForm={userProfileForm}
                        userProfile={userProfile}
                        formActions={formActions}
                        onFocus={doTagEmailFieldFocus}
                        rightSideContent={validateEmailButton}
                    />
                </div>
            </div>
        );
    }
);
ValidateEmail.displayName = 'ValidateEmail';
